/**
 * Convite de avaliação nas lojas — o registro de "quando pedir, e quando parar".
 *
 * ## O que ele é
 *
 * Um pré-prompt NOSSO (a folha `ReviewPromptSheet`) cujo CTA abre a ficha do
 * app na loja de onde esta instalação veio — no iOS direto no compositor de
 * avaliação (`?action=write-review`). Não há plugin nativo: o link externo sai
 * pelo mesmo caminho já embarcado do `ManageSubscriptionLink`
 * (`target="_blank"` numa WebView cujo host não está no `allowNavigation`
 * abre no sistema, e o sistema roteia apps.apple.com / play.google.com para a
 * própria loja). Se um dia entrar o in-app review nativo
 * (`@capacitor-community/in-app-review`), este módulo continua sendo o dono do
 * "quando" — só o CTA troca de mecanismo.
 *
 * ## Por que só no app NATIVO
 *
 * Avaliação é da LOJA. Na web/PWA não existe instalação a avaliar, e mandar a
 * pessoa para uma ficha que ela nunca usou é pedir avaliação de outra coisa.
 *
 * ## O "tempo de uso" é DIAS DISTINTOS, por aparelho
 *
 * Contador local `{last, days}`: cada dia (fuso BR, `dayKeyForBR` — regra de
 * ouro 5) em que o app abre soma 1. Três dias distintos é o piso — quem voltou
 * três dias já decidiu que o app vale alguma coisa, e é essa pessoa que a loja
 * quer ouvir. Por aparelho (não por conta) porque a pergunta é sobre ESTA
 * instalação — mesma razão da dispensa do convite de push.
 *
 * ## O fim da insistência
 *
 * "Avaliar" → nunca mais (não dá pra saber se avaliou; perguntar de novo a
 * quem já foi à loja é pior que perder um retardatário). Qualquer outra saída
 * ("Agora não", ✕, scrim) → volta UMA vez, 30 dias depois; na segunda dispensa
 * morre para sempre neste aparelho. Diferente do push (definitivo na 1ª), a
 * loja não pune a re-oferta — o teto de 2 é só respeito.
 *
 * ## Storage indisponível
 *
 * Mesmo contrato do `pushPrompt`: escrita falha em silêncio, espelho de módulo
 * segura a sessão — o pior caso é "reaparece amanhã", nunca crash.
 */

import { nativePlatform } from "../../../lib/platform/native";
import { dayKeyForBR } from "../../../lib/time";

/** Dias DISTINTOS de uso antes da primeira oferta. Dial do fundador. */
export const REVIEW_PROMPT_MIN_USAGE_DAYS = 3;
/** "Agora não" adia por este tanto. */
export const REVIEW_PROMPT_SNOOZE_DAYS = 30;
/** Depois de N dispensas, o app para de oferecer sozinho neste aparelho. */
export const REVIEW_PROMPT_MAX_OFFERS = 2;

const USAGE_KEY = "livra_review_usage";
const DONE_KEY = "livra_review_done";
const SNOOZE_KEY = "livra_review_snooze";

const SNOOZE_MS = REVIEW_PROMPT_SNOOZE_DAYS * 24 * 60 * 60 * 1000;

// ── Espelhos de módulo (storage indisponível) ─────────────────────────────
let usageInSession: { last: string; days: number } | null = null;
let doneInSession = false;
let snoozeInSession: { at: number; offers: number } | null = null;

function readJson<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* modo privado / quota — o espelho de módulo segura a sessão */
  }
}

function readUsage(): { last: string; days: number } {
  const stored = readJson<{ last: string; days: number }>(USAGE_KEY);
  // O maior dos dois: o espelho cobre a sessão em que a escrita falhou.
  if (stored && (!usageInSession || stored.days >= usageInSession.days)) return stored;
  return usageInSession ?? { last: "", days: 0 };
}

/**
 * Registra o dia de uso corrente. Chamar uma vez por abertura do app (o
 * watcher faz isso); o mesmo dia repetido é no-op — o que conta é dia
 * DISTINTO, não sessão.
 */
export function recordReviewUsageDay(dayKey: string = dayKeyForBR()): void {
  const current = readUsage();
  if (current.last === dayKey) return;
  const next = { last: dayKey, days: current.days + 1 };
  usageInSession = next;
  writeJson(USAGE_KEY, next);
}

export function markReviewPromptDone(): void {
  doneInSession = true;
  try {
    window.localStorage.setItem(DONE_KEY, "1");
  } catch {
    /* espelho segura a sessão */
  }
}

/** "Agora não" / ✕ / scrim — adia e conta a oferta gasta. */
export function snoozeReviewPrompt(now: number = Date.now()): void {
  const current = readSnooze();
  const next = { at: now, offers: current.offers + 1 };
  snoozeInSession = next;
  writeJson(SNOOZE_KEY, next);
}

function readSnooze(): { at: number; offers: number } {
  const stored = readJson<{ at: number; offers: number }>(SNOOZE_KEY);
  if (stored && (!snoozeInSession || stored.offers >= snoozeInSession.offers)) return stored;
  return snoozeInSession ?? { at: 0, offers: 0 };
}

function isDone(): boolean {
  if (doneInSession) return true;
  try {
    return window.localStorage.getItem(DONE_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Vale a pena oferecer o convite agora? Síncrona e barata de propósito — roda
 * em todo render do watcher, como `canOfferPushPrompt`.
 */
export function canOfferReviewPrompt(now: number = Date.now()): boolean {
  /*
   * Plataforma antes de tudo — e o gate é UM só, de propósito. `nativePlatform`
   * devolve `null` fora do app das lojas (ele checa `isNativePlatform()` por
   * dentro), e um `isNativeApp()` na frente foi medido como REDUNDANTE por
   * mutação — guard que parece proteger e não protege é pior que nenhum.
   *
   * ⚠️ A EXISTÊNCIA da ficha (`STORES` vazia = rollback) NÃO é checada aqui, e
   * isso é custo de bundle, não descuido: este módulo roda em todo render do
   * watcher no chunk de ENTRADA, e `appStores` arrasta a prosa inteira do
   * conteúdo público. Quem cobre o rollback é a folha (lazy), que se fecha
   * sozinha sem destino — ver `reviewStoreLink.ts`.
   */
  if (nativePlatform() === null) return false;

  if (isDone()) return false;

  const snooze = readSnooze();
  if (snooze.offers >= REVIEW_PROMPT_MAX_OFFERS) return false;
  if (snooze.at > 0 && now - snooze.at < SNOOZE_MS) return false;

  return readUsage().days >= REVIEW_PROMPT_MIN_USAGE_DAYS;
}

/** Só para os testes — zera memória e espelhos. */
export function __resetReviewPromptForTests(): void {
  usageInSession = null;
  doneInSession = false;
  snoozeInSession = null;
  try {
    window.localStorage.removeItem(USAGE_KEY);
    window.localStorage.removeItem(DONE_KEY);
    window.localStorage.removeItem(SNOOZE_KEY);
  } catch {
    /* idem */
  }
}
